import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["step", "indicator", "scheduledAt", "shortage", "notice", "noticeText", "alternativesBox",
    "alternatives", "servicesNext", "review"]
  static values = { url: String, capacityUrl: String, start: { type: Number, default: 1 } }

  connect() {
    this.show(this.startValue - 1)
  }

  disconnect() {
    clearTimeout(this.checkTimer)
  }

  next() {
    if (!this.currentValid()) return
    if (this.current === 1 && this.blocked) return

    if (this.current === 1) this.fillReview()
    this.show(this.current + 1)
  }

  previous() {
    this.show(this.current - 1)
  }

  show(index) {
    this.current = Math.max(0, Math.min(index, this.stepTargets.length - 1))
    this.stepTargets.forEach((step, position) => { step.hidden = position !== this.current })
    this.indicatorTargets.forEach((indicator, position) => {
      indicator.toggleAttribute("aria-current", position === this.current)
      indicator.firstElementChild.classList.toggle("tenant-bg", position <= this.current)
    })
    if (this.current === 1) this.check()
  }

  currentValid() {
    const fields = this.stepTargets[this.current].querySelectorAll("input, select")
    return Array.from(fields).every((field) => field.reportValidity())
  }

  check() {
    clearTimeout(this.checkTimer)
    this.checkTimer = setTimeout(() => this.fetchAvailability(), 300)
  }

  get scheduledAtInput() {
    return this.scheduledAtTarget.querySelector("input[name='request[scheduled_at]']")
  }

  async fetchAvailability() {
    if (!this.scheduledAtInput.value) return

    this.refreshCapacity(this.scheduledAtInput.value)

    const params = new URLSearchParams(new FormData(this.element))
    params.delete("authenticity_token")
    try {
      const response = await fetch(`${this.urlValue}?${params}`, { headers: { Accept: "application/json" } })
      this.render(await response.json())
    } catch (error) {
      console.error("cco-request-steps", error)
    }
  }

  refreshCapacity(scheduledAt) {
    if (!this.hasCapacityUrlValue || scheduledAt === this.capacityFrom) return

    const frame = document.getElementById("cco_capacity")
    if (!frame) return

    this.capacityFrom = scheduledAt
    frame.src = `${this.capacityUrlValue}?${new URLSearchParams({ from: scheduledAt })}`
  }

  render(data) {
    this.shortageTargets.forEach((element) => element.classList.add("hidden"))
    this.alternativesTarget.replaceChildren()
    this.alternativesBoxTarget.classList.add("hidden")

    const message = data.error || data.advance_error
    this.blocked = Boolean(message) || data.fits === false
    this.servicesNextTarget.disabled = this.blocked
    this.servicesNextTarget.classList.toggle("opacity-50", this.blocked)

    if (!this.blocked) return this.noticeTarget.classList.add("hidden")

    this.noticeTarget.classList.remove("hidden")
    this.noticeTextTarget.textContent = message || (data.shortages || []).map((shortage) => shortage.message).join(" ")
    ;(data.shortages || []).forEach((shortage) => {
      const target = this.shortageTargets.find((element) => element.dataset.categoryId === String(shortage.category_id))
      if (!target) return
      target.textContent = shortage.message
      target.classList.remove("hidden")
    })
    this.renderAlternatives(data.alternatives || [])
  }

  renderAlternatives(alternatives) {
    if (alternatives.length === 0) return

    this.alternativesBoxTarget.classList.remove("hidden")
    alternatives.forEach(({ value, label }) => {
      const button = document.createElement("button")
      button.type = "button"
      button.textContent = label
      button.dataset.value = value
      button.className = "rounded-md border border-amber-300 bg-white px-3 py-1 text-sm font-semibold text-amber-900 hover:bg-amber-100"
      button.addEventListener("click", () => this.choose(value))
      this.alternativesTarget.append(button)
    })
  }

  choose(value) {
    const [date, time] = value.split("T")
    const [year, month, day] = date.split("-").map(Number)
    const [hours, minutes] = time.split(":").map(Number)
    const wrapper = this.scheduledAtTarget.querySelector("[data-controller~='date-time-picker']")
    this.application.getControllerForElementAndIdentifier(wrapper, "date-time-picker")
      ?.setDate(new Date(year, month - 1, day, hours, minutes))
  }

  fillReview() {
    const text = (name) => this.element.querySelector(`[data-review='${name}']`)?.value || ""
    const company = this.element.querySelector("[data-review='company']")
    if (company) this.setReview("company", company.selectedOptions[0]?.text || "")
    this.setReview("vessel", text("vessel"))
    this.setReview("destination", text("destination"))
    this.setReview("scheduled_at", this.scheduledAtTarget.querySelector("[data-date-time-picker-target='label']").textContent)

    const services = Array.from(this.element.querySelectorAll("[data-review-service]")).filter((block) => {
      return block.querySelector("[data-review='selected'][type='checkbox']")?.checked
    }).map((block) => {
      const quantity = block.querySelector("[data-review='quantity']").value
      const type = block.querySelector("[data-review='service_type']")
      return `${block.dataset.reviewService} ×${quantity} · ${type.selectedOptions[0]?.text || ""}`
    })
    this.setReview("services", services.join(", "))
  }

  setReview(name, value) {
    const element = this.reviewTarget.querySelector(`[data-review-value='${name}']`)
    if (element) element.textContent = value
  }
}
